import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import { blogPosts } from '../data/mockData';

export const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(blogPosts.map((post) => post.category))];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-[rgb(17,17,19)] min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-6 py-2 bg-[rgba(0,250,99,0.1)] border border-[rgba(0,250,99,0.3)] rounded-full mb-6">
              <span className="text-[rgb(0,250,99)] text-sm font-medium">
                Insights & Resources
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Healthcare Marketing Insights
            </h1>
            <p className="text-xl text-[rgb(218,218,218)]">
              Expert perspectives on strategy, growth, and healthcare brand communication
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="pb-12">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search Bar */}
              <div className="relative w-full md:w-96">
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[rgb(161,161,170)]"
                />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(0,250,99)] transition-colors"
                />
              </div>

              {/* Category Filter */}
              <div className="flex gap-2 flex-wrap justify-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                      selectedCategory === category
                        ? 'bg-[rgb(0,250,99)] text-[rgb(17,17,19)]'
                        : 'bg-[rgb(26,28,30)] text-[rgb(218,218,218)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(0,250,99)]'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            {filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 hover:border-[rgb(0,250,99)]"
                  >
                    {/* Post Image */}
                    <div className="relative h-56 overflow-hidden bg-[rgb(38,40,42)]">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[rgb(0,250,99)] text-[rgb(17,17,19)] text-xs font-semibold rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="p-6">
                      <div className="flex items-center space-x-4 text-sm text-[rgb(161,161,170)] mb-4">
                        <div className="flex items-center space-x-1">
                          <Calendar size={14} />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>

                      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[rgb(0,250,99)] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-[rgb(161,161,170)] text-sm leading-relaxed mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="text-[rgb(161,161,170)] text-sm">
                          {post.author}
                        </span>
                        <div className="flex items-center space-x-1 text-[rgb(0,250,99)] font-medium text-sm group/link">
                          <span>Read More</span>
                          <ArrowRight
                            size={16}
                            className="group-hover/link:translate-x-1 transition-transform"
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-[rgb(161,161,170)] text-lg">
                  No articles found matching your search.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
